import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type BrandFacts, type BrandMedia, type ContentPlan, type PlanComment, type PlanItem, type PlanMonth, type PlanSummary, type Promotion } from '../api';
import { Icon } from '../components/icons';
import { PageBar } from '../components/PageBar';
import { Chip, ErrorBox, errorMessage, Field, Spinner, useToast } from '../components/ui';
import { t, tMaybe, type Key } from '../i18n';
import { fmtShort } from '../lib/format';
import { useSession } from '../lib/session';
import '../styles/plan.css';

/**
 * The agent's monthly content plan. The client reads what the month is about (each post's date, theme, occasion, the facts it will use
 * and the photo), comments on the plan or on one post, asks for changes, and approves. Drafts for the month are made from the approved
 * plan. Below it, what the client wants pushed (promotions), which steers the next plan.
 */

const monthOf = (d: DateTime) => d.toFormat('yyyy-LL');
const monthLabel = (m: string) => DateTime.fromISO(`${m}-01`).toFormat('LLLL yyyy');
const dayLabel = (d: string) => DateTime.fromISO(d).toFormat('ccc d LLL');

function useMonths(brandId: string) {
  const plans = useQuery({ queryKey: ['plans', brandId], queryFn: () => api.get<PlanSummary[]>(`/api/brands/${brandId}/plans`) });
  return useMemo(() => {
    const now = DateTime.now();
    const set = new Set([monthOf(now), monthOf(now.plus({ months: 1 })), monthOf(now.plus({ months: 2 })), ...(plans.data ?? []).map((p) => p.month)]);
    return [...set].sort();
  }, [plans.data]);
}

export function PlanPage() {
  const { brand, can } = useSession();
  const [params, setParams] = useSearchParams();
  const months = useMonths(brand.id);
  const month = params.get('month') ?? months.find((m) => m > monthOf(DateTime.now())) ?? monthOf(DateTime.now());
  const qc = useQueryClient();
  const toast = useToast();

  const data = useQuery({ queryKey: ['plan', brand.id, month], queryFn: () => api.get<PlanMonth>(`/api/brands/${brand.id}/plans?month=${month}`) });
  const facts = useQuery({ queryKey: ['facts', brand.id], queryFn: () => api.get<BrandFacts>(`/api/brands/${brand.id}/facts`) });
  const media = useQuery({ queryKey: ['media', brand.id], queryFn: () => api.get<BrandMedia[]>(`/api/brands/${brand.id}/media`) });
  const promos = useQuery({ queryKey: ['promotions', brand.id], queryFn: () => api.get<Promotion[]>(`/api/brands/${brand.id}/promotions`) });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['plan', brand.id, month] });
    qc.invalidateQueries({ queryKey: ['plans', brand.id] });
    qc.invalidateQueries({ queryKey: ['overview', brand.id] });
  };
  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/api/plans/${id}/approve`),
    onSuccess: () => { refresh(); toast(t('plan.approvedToast')); },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const requestChanges = useMutation({
    mutationFn: (id: string) => api.post(`/api/plans/${id}/request-changes`),
    onSuccess: () => { refresh(); toast(t('plan.changesToast')); },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  const plan = data.data?.latest ?? null;
  const comments = data.data?.comments ?? [];
  const factText = useMemo(() => {
    const all = [...(facts.data?.published?.facts ?? []), ...(facts.data?.approved?.content.facts ?? [])];
    return new Map(all.map((f) => [f.id, f.text]));
  }, [facts.data]);
  const mediaById = useMemo(() => new Map((media.data ?? []).map((m) => [m.id, m])), [media.data]);
  const promoById = useMemo(() => new Map((promos.data ?? []).map((p) => [p.id, p])), [promos.data]);
  const open = comments.filter((c) => c.plan_id === plan?.id && !c.parent_id && c.status === 'open');
  const decidable = plan && (plan.state === 'in_review' || plan.state === 'approved');

  return (
    <>
      <PageBar crumbs={[{ label: t('plan.title') }]} />
      <div className="plan-page">
        <header className="plan-head">
          <div>
            <h1>{t('plan.title')}</h1>
            <p className="muted small">{t('plan.intro')}</p>
          </div>
          <label className="plan-month">
            <span className="small muted">{t('plan.month')}</span>
            <select value={month} onChange={(e) => setParams({ month: e.target.value })}>
              {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
          </label>
        </header>

        {data.error && <ErrorBox error={data.error} />}
        {data.isLoading && <Spinner />}
        {data.data && !plan && <p className="card plan-empty muted">{t('plan.none')}</p>}

        {plan && (
          <section className="card plan-card" aria-labelledby="plan-summary">
            <div className="plan-status">
              <Chip state={plan.state === 'in_review' ? 'in_review' : plan.state} label={t(`plan.state.${plan.state}` as Key)} />
              <span className="small muted">
                {t('plan.version', { n: plan.number })} · {plan.by_agent ? t('plan.byAgent') : t('plan.by', { who: plan.author ?? '' })} · {fmtShort(plan.created_at)}
                {' · '}{t('plan.posts', { count: plan.items.length })}
              </span>
            </div>
            <h2 id="plan-summary" className="plan-summary">{plan.summary}</h2>
            {plan.state === 'approved' && plan.decided_at && <p className="small">{t('plan.approvedBy', { who: plan.decided_by ?? '', when: fmtShort(plan.decided_at) })}</p>}
            {plan.state === 'in_review' && data.data?.approved && data.data.approved.id !== plan.id && (
              <p className="small muted">{t('plan.approvedStill', { n: data.data.approved.number })}</p>
            )}

            {plan.questions.length > 0 && (
              <div className="plan-questions">
                <h3><Icon name="bot" size={15} /> {t('plan.questions')}</h3>
                <ul>{plan.questions.map((q) => <li key={q.id}>{q.question}</li>)}</ul>
                <p className="small muted">{t('plan.answerHint')}</p>
              </div>
            )}

            <ol className="plan-items">
              {plan.items.map((it) => (
                <PlanItemRow key={it.key} plan={plan} item={it} factText={factText} media={mediaById.get(it.asset_id ?? '') ?? null}
                  promotion={it.promotion_id ? promoById.get(it.promotion_id)?.title ?? null : null}
                  comments={comments.filter((c) => c.item_key === it.key || (c.parent_id && comments.find((p) => p.id === c.parent_id)?.item_key === it.key))}
                  allComments={comments} canComment={can('comment') && !!decidable} onDone={refresh} />
              ))}
            </ol>

            <div className="plan-general">
              <h3>{t('plan.commentPlan')}</h3>
              <Thread comments={comments.filter((c) => !c.item_key && (!c.parent_id || !comments.find((p) => p.id === c.parent_id)?.item_key))} all={comments} revisions={data.data?.revisions ?? []} onDone={refresh} />
              {can('comment') && decidable && <CommentBox planId={plan.id} itemKey={null} onDone={refresh} />}
            </div>

            {decidable && (can('approve') || can('requestChanges')) && (
              <div className="plan-actions">
                {open.length > 0 && <span className="small">{t('plan.openComments', { count: open.length })}</span>}
                {can('requestChanges') && open.length > 0 && (
                  <button type="button" className="btn" disabled={requestChanges.isPending} onClick={() => requestChanges.mutate(plan.id)}>{t('plan.requestChanges')}</button>
                )}
                {can('approve') && plan.state === 'in_review' && (
                  <button type="button" className="btn btn-primary" disabled={approve.isPending || open.length > 0} onClick={() => approve.mutate(plan.id)}>
                    <Icon name="check" size={14} /> {t('plan.approve')}
                  </button>
                )}
              </div>
            )}
          </section>
        )}

        {data.data && data.data.revisions.length > 1 && (
          <section className="card plan-card">
            <details>
              <summary>{t('plan.history')}</summary>
              <ul className="ent-list">
                {data.data.revisions.slice(1).map((r) => (
                  <li key={r.id} className="small">{t('plan.historyRow', { n: r.number, state: t(`plan.state.${r.state}` as Key), when: fmtShort(r.created_at) })} — {r.summary}</li>
                ))}
              </ul>
            </details>
          </section>
        )}

        <Promotions brandId={brand.id} canEdit={can('approve')} list={promos.data ?? []} error={promos.error} />
      </div>
    </>
  );
}

function PlanItemRow({ plan, item, factText, media, promotion, comments, allComments, canComment, onDone }: {
  plan: ContentPlan; item: PlanItem; factText: Map<string, string>; media: BrandMedia | null; promotion: string | null;
  comments: PlanComment[]; allComments: PlanComment[]; canComment: boolean; onDone: () => void;
}) {
  const [commenting, setCommenting] = useState(false);
  return (
    <li className="plan-item" id={`item-${item.key}`}>
      <div className="plan-item-when">
        <strong>{dayLabel(item.day)}</strong>
        <span className="small muted">{item.network}</span>
      </div>
      <div className="plan-item-photo">
        {media?.kind === 'image' && media.url ? <img src={media.url} alt={media.name} /> : media ? <span className="plan-video"><Icon name="film" size={22} /></span> : <span className="plan-nophoto small muted">{t('plan.noPhoto')}</span>}
        {media && <span className="small muted plan-photo-name">{media.name}</span>}
      </div>
      <div className="plan-item-body">
        <div className="plan-item-tags">
          <span className="chip">{tMaybe(`plan.type.${item.content_type}`, item.content_type.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()))}</span>
          {item.occasion && <span className="chip plan-occasion">{item.occasion}</span>}
          {promotion && <span className="chip plan-promo">{t('plan.promotion', { title: promotion })}</span>}
        </div>
        <h3 className="plan-theme">{item.theme}</h3>
        {item.buyer && <p className="small plan-buyer">{t('plan.buyer', { who: item.buyer })}</p>}
        <p>{item.angle}</p>
        <details className="plan-facts">
          <summary className="small">{t('plan.facts')} ({item.fact_ids.length})</summary>
          {item.fact_ids.length === 0 ? <p className="small muted">{t('plan.noFacts')}</p> : (
            <ul>{item.fact_ids.map((id) => <li key={id} className="small"><code>{id}</code> {factText.get(id) ?? ''}</li>)}</ul>
          )}
        </details>
        <Thread comments={comments} all={allComments} revisions={[plan]} onDone={onDone} />
        {canComment && (commenting
          ? <CommentBox planId={plan.id} itemKey={item.key} onDone={() => { setCommenting(false); onDone(); }} />
          : <button type="button" className="btn btn-small btn-ghost" onClick={() => setCommenting(true)}><Icon name="bubble" size={14} /> {t('plan.commentItem')}</button>)}
      </div>
    </li>
  );
}

function Thread({ comments, all, revisions, onDone }: { comments: PlanComment[]; all: PlanComment[]; revisions: ContentPlan[]; onDone: () => void }) {
  const { can } = useSession();
  const toast = useToast();
  const resolve = useMutation({
    mutationFn: (id: string) => api.post(`/api/plan-comments/${id}/resolve`),
    onSuccess: onDone,
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const tops = comments.filter((c) => !c.parent_id);
  if (!tops.length) return null;
  const numberOf = (id: string | null) => revisions.find((r) => r.id === id)?.number;
  return (
    <ul className="plan-thread">
      {tops.map((c) => (
        <li key={c.id} className={`plan-comment ${c.status}`}>
          <p><strong>{c.author}</strong> <span className="small muted">{fmtShort(c.created_at)}</span>
            {c.status === 'resolved' && <span className="small muted"> · {c.resolved_in_plan_id ? t('plan.resolved', { n: numberOf(c.resolved_in_plan_id) ?? '?' }) : t('plan.closed')}</span>}
          </p>
          <p>{c.body}</p>
          {all.filter((r) => r.parent_id === c.id).map((r) => (
            <p key={r.id} className={`plan-reply${r.by_agent ? ' by-agent' : ''}`}>{r.by_agent && <Icon name="bot" size={15} />} <strong>{r.author}</strong> {r.body}</p>
          ))}
          {c.status === 'open' && can('resolve') && (
            <button type="button" className="btn btn-small btn-ghost" disabled={resolve.isPending} onClick={() => resolve.mutate(c.id)}>{t('plan.resolve')}</button>
          )}
        </li>
      ))}
    </ul>
  );
}

function CommentBox({ planId, itemKey, onDone }: { planId: string; itemKey: string | null; onDone: () => void }) {
  const [body, setBody] = useState('');
  const toast = useToast();
  const send = useMutation({
    mutationFn: () => api.post(`/api/plans/${planId}/comments`, { body, item_key: itemKey }),
    onSuccess: () => { setBody(''); onDone(); },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  return (
    <form className="plan-commentbox" onSubmit={(e) => { e.preventDefault(); if (body.trim()) send.mutate(); }}>
      <textarea value={body} maxLength={2000} placeholder={t('plan.commentPlaceholder')} onChange={(e) => setBody(e.target.value)} aria-label={itemKey ? t('plan.commentItem') : t('plan.commentPlan')} />
      <button type="submit" className="btn btn-small btn-primary" disabled={send.isPending || !body.trim()}>{t('plan.send')}</button>
    </form>
  );
}

function Promotions({ brandId, canEdit, list, error }: { brandId: string; canEdit: boolean; list: Promotion[]; error: unknown }) {
  const qc = useQueryClient();
  const toast = useToast();
  const empty = { title: '', details: '', starts_on: DateTime.now().toISODate()!, ends_on: DateTime.now().plus({ weeks: 4 }).toISODate()! };
  const [form, setForm] = useState(empty);
  const refresh = () => qc.invalidateQueries({ queryKey: ['promotions', brandId] });
  const add = useMutation({
    mutationFn: () => api.post<Promotion>(`/api/brands/${brandId}/promotions`, form),
    onSuccess: () => { setForm(empty); refresh(); toast(t('promo.added')); },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const archive = useMutation({
    mutationFn: (id: string) => api.patch(`/api/promotions/${id}`, { archived: true }),
    onSuccess: () => { refresh(); toast(t('promo.archived')); },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const live = list.filter((p) => !p.archived_at);
  return (
    <section className="card plan-card" aria-labelledby="promo-title">
      <h2 id="promo-title">{t('promo.title')}</h2>
      <p className="small muted">{t('promo.intro')}</p>
      {!!error && <ErrorBox error={error} />}
      {live.length === 0 ? <p className="small muted">{t('promo.none')}</p> : (
        <ul className="plan-promos">
          {live.map((p) => (
            <li key={p.id}>
              <div><strong>{p.title}</strong> <span className="small muted">{t('promo.range', { from: dayLabel(p.starts_on), to: dayLabel(p.ends_on) })}</span></div>
              {p.details && <p className="small">{p.details}</p>}
              {canEdit && <button type="button" className="btn btn-small btn-ghost" disabled={archive.isPending} onClick={() => archive.mutate(p.id)}>{t('promo.archive')}</button>}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <form className="plan-promo-form" onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
          <Field label={t('promo.name')}><input type="text" required maxLength={120} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label={t('promo.details')}><textarea maxLength={1000} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} /></Field>
          <div className="row">
            <Field label={t('promo.starts')}><input type="date" required value={form.starts_on} onChange={(e) => setForm({ ...form, starts_on: e.target.value })} /></Field>
            <Field label={t('promo.ends')}><input type="date" required value={form.ends_on} min={form.starts_on} onChange={(e) => setForm({ ...form, ends_on: e.target.value })} /></Field>
          </div>
          <button type="submit" className="btn btn-primary" disabled={add.isPending}>{t('promo.add')}</button>
        </form>
      )}
    </section>
  );
}
